import type { DateTime } from '@piccolohealth/util';
import objectHash from 'object-hash';
import * as Y from 'yjs';

export * as Y from 'yjs';

export const TIPTAP_CONTENT_KEY = 'default';
export const SCORING_CHECKLIST_KEY = 'scoringChecklist';
export const COMMENT_THREADS_KEY = 'commentThreads';

export interface VersionMinimal {
  versionId: string;
  createdAt: DateTime;
}

export const createDocFromUint8Array = (buffer: Uint8Array): Y.Doc => {
  const doc = new Y.Doc({ gc: false });
  Y.applyUpdate(doc, new Uint8Array(buffer));
  return doc;
};

export const createSnapshotFromDoc = (doc: Y.Doc): Y.Snapshot => {
  return Y.snapshot(doc);
};

export const createSnapshotFromUint8Array = (buffer: Uint8Array): Y.Snapshot => {
  return Y.decodeSnapshot(buffer);
};

export const createUint8ArrayFromDoc = (doc: Y.Doc): Uint8Array => {
  return Y.encodeStateAsUpdate(doc);
};

export const createPrunedUint8ArrayFromDoc = (doc: Y.Doc): Uint8Array => {
  const prunedDoc = new Y.Doc({ gc: true });
  const update = Y.encodeStateAsUpdate(doc);
  Y.applyUpdate(prunedDoc, update);
  return Y.encodeStateAsUpdate(prunedDoc);
};

export const createUint8ArrayFromSnapshot = (snapshot: Y.Snapshot): Uint8Array => {
  return Y.encodeSnapshot(snapshot);
};

export function revertChangesSinceSnapshot(doc: Y.Doc, snapshot: Y.Snapshot) {
  const YJS_SNAPSHOT_ORIGIN = 'yjs-snapshot';

  const tempDoc = Y.createDocFromSnapshot(doc, snapshot);

  // We cannot simply replace `this.yDoc` because we have to sync with other clients.
  // Replacing `this.yDoc` would be similar to doing `git reset --hard $SNAPSHOT && git push --force`.
  // Instead, we compute an "anti-operation" of all the changes made since that snapshot.
  // This ends up being similar to `git revert $SNAPSHOT..HEAD`.
  const currentStateVector = Y.encodeStateVector(doc);
  const snapshotStateVector = Y.encodeStateVector(tempDoc);

  // Creating undo command encompassing all changes since taking snapshot
  const changesSinceSnapshotUpdate = Y.encodeStateAsUpdate(doc, snapshotStateVector);
  const um = new Y.UndoManager(tempDoc.getXmlFragment(TIPTAP_CONTENT_KEY), {
    trackedOrigins: new Set([YJS_SNAPSHOT_ORIGIN]),
  });
  Y.applyUpdate(tempDoc, changesSinceSnapshotUpdate, YJS_SNAPSHOT_ORIGIN);
  um.undo();

  // Applying undo command to this.ydoc
  const revertChangesSinceSnapshotUpdate = Y.encodeStateAsUpdate(tempDoc, currentStateVector);
  Y.applyUpdate(doc, revertChangesSinceSnapshotUpdate, YJS_SNAPSHOT_ORIGIN);
}

/**
 * Compute the md5 hash of the content and comments of a document.
 * Truncate the hash to 8 characters.
 */
export const computeDocumentHash = (doc: Y.Doc) => {
  const content = doc.getXmlFragment(TIPTAP_CONTENT_KEY).toJSON();
  const comments = doc.getMap(COMMENT_THREADS_KEY).toJSON();
  const scoringChecklist = doc.getMap(SCORING_CHECKLIST_KEY).toJSON();

  return objectHash({
    content,
    comments,
    scoringChecklist,
  }).slice(0, 8);
};

export const getPrunableVersions = (versions: VersionMinimal[]): string[] => {
  const FIVE_MIN = 5 * 60 * 1000; // ms

  const prunable: string[] = [];
  let clusterAnchorTime: DateTime | null = null;
  let lastKeptTime: DateTime | null = null;

  for (const version of versions) {
    if (!clusterAnchorTime) {
      // start new cluster
      clusterAnchorTime = version.createdAt;
      lastKeptTime = version.createdAt;
      // keep newest in cluster
      continue;
    }

    const gapFromAnchor = clusterAnchorTime.diff(version.createdAt).toMillis();
    const gapFromLastKept = lastKeptTime!.diff(version.createdAt).toMillis();

    if (gapFromLastKept > FIVE_MIN) {
      // Big jump, start a new cluster
      clusterAnchorTime = version.createdAt;
      lastKeptTime = version.createdAt;
      // keep it (new cluster anchor)
      continue;
    }

    if (gapFromAnchor > FIVE_MIN) {
      // Beyond total 5m span, keep it and reset anchor
      clusterAnchorTime = version.createdAt;
      lastKeptTime = version.createdAt;
      continue;
    }

    // Otherwise within the current cluster span, mark for deletion
    prunable.push(version.versionId);
  }

  return prunable;
};
